import { useCallback, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useSelectedAccount } from '@ibk/shared-auth';
import { OperatorRoles, RegisterOperatorErrors, SuperAppProduct, } from '@ibk/shared-types';
import { formatAsRegionalPhoneCode, isValidCpf, isValidFullName, isValidPhoneNumber, maskCpf, operatorDrawerErrorAtom, operatorDrawerNavigationAtom, operatorDrawerSuccessAtom, unmaskCpf, validateAndRemoveNumbers, validateEmail, } from '@ibk/shared-utils';
import { useLazyGetAccountInfoQuery, useLazyRegisterOperatorQuery } from '../../services/operatorsApi';
import { getTransactionLimitConditions } from './RegisterForm.mocks';
const initialFormState = {
    fullName: '',
    document: '',
    email: '',
    phone: '',
    blockFields: false,
    blockEmailPhone: false,
};
const initialFormBlurredFields = {
    fullName: false,
    document: false,
    email: false,
    phone: false,
};
const initialFormErrorsState = {
    fullName: false,
    document: false,
    email: false,
    phone: false,
};
const handleOperatorRegisterErrorMessages = (t, errorMessage) => {
    if (errorMessage === RegisterOperatorErrors.OPERATOR_ALREADY_REGISTERED) {
        return t('operator.drawer.pages.toast-add-operator-error-exists');
    }
    return t('operator.drawer.pages.toast-add-operator-error');
};
const shouldBlockFields = (hasAccess, availableApps = []) => {
    const hasParaVoce = availableApps.includes(SuperAppProduct.PARA_VOCE);
    const hasMinhaEmpresa = availableApps.includes(SuperAppProduct.MINHA_EMPRESA);
    const hasMeuEscritorio = availableApps.includes(SuperAppProduct.MEU_ESCRITORIO);
    if (hasMinhaEmpresa || hasMeuEscritorio) {
        return true;
    }
    if (hasParaVoce) {
        return hasAccess;
    }
    return true;
};
export const useRegisterHook = (t, testId, params) => {
    const role = params?.role ?? OperatorRoles.OPERATOR;
    const transactionLimit = params?.transactionLimit ?? undefined;
    const requiredFieldError = `${testId}.error.required-fields`;
    const [formFields, setFormFields] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState(initialFormErrorsState);
    const [formBlurredFields, setFormBlurredFields] = useState(initialFormBlurredFields);
    const [checked, setChecked] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigateTo = useSetAtom(operatorDrawerNavigationAtom);
    const setError = useSetAtom(operatorDrawerErrorAtom);
    const setSuccess = useSetAtom(operatorDrawerSuccessAtom);
    const [getAccountInfo] = useLazyGetAccountInfoQuery();
    const [registerOperator] = useLazyRegisterOperatorQuery();
    const { selectedAccount } = useSelectedAccount();
    const cleanUpStates = () => {
        setFormFields(initialFormState);
        setFormErrors(initialFormErrorsState);
        setFormBlurredFields(initialFormBlurredFields);
        setChecked(false);
        setShowTermsModal(false);
        setLoading(false);
    };
    useEffect(() => {
        return () => {
            cleanUpStates();
        };
    }, []);
    const handleCheckTerms = () => {
        setChecked(prevChecked => !prevChecked);
    };
    const handleDocumentRequest = async (value) => {
        if (value.length === 14 && isValidCpf(value) && !loading) {
            try {
                setLoading(true);
                const response = await getAccountInfo({ document: unmaskCpf(value) });
                const accountData = response.data?.[0];
                if (accountData) {
                    const shouldBlockEmailPhone = shouldBlockFields(accountData.has_access, accountData.available_apps);
                    const cleanedFullName = validateAndRemoveNumbers(accountData.full_name ?? '');
                    const cleanedEmail = accountData.email?.trim() ?? '';
                    const cleanedPhone = accountData.phone_number?.replace('+55', '') ?? '';
                    setFormFields(prev => ({
                        ...prev,
                        fullName: cleanedFullName,
                        email: cleanedEmail,
                        phone: cleanedPhone,
                        blockFields: true,
                        blockEmailPhone: shouldBlockEmailPhone,
                    }));
                }
                else {
                    setFormFields(prev => ({ ...prev, fullName: '', email: '', phone: '' }));
                }
            }
            finally {
                setLoading(false);
            }
        }
    };
    const handleInputChange = (name, value) => {
        switch (name) {
            case 'document':
                setFormFields(prev => ({ ...prev, document: maskCpf(value), blockFields: false, blockEmailPhone: false }));
                validateInputError(name, maskCpf(value));
                handleDocumentRequest(maskCpf(value));
                break;
            case 'fullName':
                setFormFields(prev => ({ ...prev, [name]: validateAndRemoveNumbers(value) }));
                validateInputError(name, validateAndRemoveNumbers(value));
                break;
            case 'phone':
                setFormFields(prev => ({ ...prev, [name]: value }));
                validateInputError(name, value);
                break;
            case 'email':
                setFormFields(prev => ({ ...prev, [name]: value?.trim() ?? '' }));
                validateInputError(name, value);
                break;
        }
    };
    const validateInputError = (name, value, blurred) => {
        switch (name) {
            case 'fullName':
                setFormErrors(prev => ({
                    ...prev,
                    fullName: !isValidFullName(value) && (formBlurredFields.fullName || blurred) ? requiredFieldError : false,
                }));
                break;
            case 'document': {
                let documentError = false;
                if (value.length === 14) {
                    documentError = isValidCpf(value) ? false : `${testId}.error.document`;
                }
                else if (formBlurredFields.document || blurred) {
                    documentError = requiredFieldError;
                }
                setFormErrors(prev => ({
                    ...prev,
                    document: documentError,
                }));
                break;
            }
            case 'email':
                setFormErrors(prev => ({
                    ...prev,
                    email: !validateEmail(value) && (formBlurredFields.email || blurred) ? requiredFieldError : false,
                }));
                break;
            case 'phone':
                setFormErrors(prev => ({
                    ...prev,
                    phone: !isValidPhoneNumber(value) && (formBlurredFields.phone || blurred) ? requiredFieldError : false,
                }));
                break;
        }
    };
    const handleBlur = (name) => {
        setFormBlurredFields(prev => ({ ...prev, [name]: true }));
        validateInputError(name, formFields[name], true);
    };
    const isDisabled = useCallback(() => !checked ||
        loading ||
        !!Object.values(formErrors).find(error => error) ||
        formFields.fullName.length < 3 ||
        !isValidCpf(formFields.document) ||
        !validateEmail(formFields.email) ||
        !isValidPhoneNumber(formFields.phone), [checked, formErrors, loading, formFields]);
    const handleComplete = useCallback(async () => {
        if (isDisabled())
            return;
        setLoading(true);
        try {
            const operator = {
                organizationId: selectedAccount?.entityId,
                documentNumber: unmaskCpf(formFields.document),
                email: formFields.email,
                phoneNumber: formatAsRegionalPhoneCode(formFields.phone),
                name: formFields.fullName,
                role: role,
            };
            if (params?.restrictedPolicies?.length) {
                operator.restrictedPolicies = params.restrictedPolicies;
            }
            if (transactionLimit) {
                const conditions = getTransactionLimitConditions(transactionLimit);
                if (Object.keys(conditions).length > 0) {
                    operator.conditions = conditions;
                }
            }
            const response = await registerOperator({ payload: operator });
            if (response.isSuccess) {
                setSuccess(t('operator.drawer.pages.toast-add-operator-success'));
            }
            else {
                const directError = response.error;
                const composedError = response.error;
                const errorMessage = handleOperatorRegisterErrorMessages(t, typeof directError.details === 'string' ? directError.details : composedError?.details?.result?.error);
                setError(errorMessage);
            }
        }
        catch (error) {
            setError(t('operator.drawer.pages.toast-add-operator-error'));
        }
        finally {
            navigateTo('clear');
            setLoading(false);
        }
    }, [
        formFields.document,
        formFields.email,
        formFields.fullName,
        formFields.phone,
        isDisabled,
        navigateTo,
        params,
        registerOperator,
        role,
        selectedAccount,
        setError,
        setSuccess,
        t,
        transactionLimit,
    ]);
    const states = {
        formFields,
        formErrors,
        checked,
        showTermsModal,
        nextDisabled: isDisabled(),
        loading,
    };
    const actions = {
        handleComplete,
        handleCheckTerms,
        setShowTermsModal,
        handleInputChange,
        handleBlur,
    };
    return {
        states,
        actions,
    };
};
